// Makario product offerings — productized vertical solutions.
// Each product has a dedicated detail page at /products/:slug for SEO long-tail.

export const PRODUCTS_LIST = [
  {
    slug: "agentic-ai-erp",
    name: "Agentic AI for ERP",
    code: "AAE",
    accent: "#8174ff",
    tagline: {
      en: "Autonomous AI agents that act inside your ERP — not just answer questions about it.",
      id: "Agen AI otonom yang bertindak di dalam ERP Anda — bukan sekadar menjawab pertanyaan tentangnya.",
    },
    intro: {
      en: "We deploy task-specific AI agents on top of your existing ERP — SAP, Odoo, Oracle, or custom — so finance closes, stock reconciliations, and approval workflows run on autopilot, with a full audit trail and human override.",
      id: "Kami menyematkan agen AI spesifik tugas di atas ERP Anda — SAP, Odoo, Oracle, atau kustom — sehingga tutup buku, rekonsiliasi stok, dan alur persetujuan berjalan otomatis, dengan jejak audit penuh dan kendali manusia.",
    },
    capabilities: {
      en: [
        "Autonomous month-end close & journal posting",
        "Three-way match (PO ↔ GR ↔ Invoice) with exception escalation",
        "Inventory reconciliation across warehouses",
        "Cash-flow forecasting with scenario simulation",
        "Natural-language ERP search (Bahasa Indonesia)",
        "Audit-grade event log with human override",
      ],
      id: [
        "Tutup buku akhir bulan & posting jurnal otonom",
        "Three-way match (PO ↔ GR ↔ Faktur) dengan eskalasi pengecualian",
        "Rekonsiliasi inventaris lintas gudang",
        "Peramalan arus kas dengan simulasi skenario",
        "Pencarian ERP berbahasa Indonesia (natural language)",
        "Log audit kelas enterprise dengan kendali manusia",
      ],
    },
    bestFor: {
      en: ["Manufacturers with 3+ plants", "Distributors with multi-warehouse stock", "Finance teams closing books weekly", "Groups consolidating multi-entity reports"],
      id: ["Manufaktur dengan 3+ pabrik", "Distributor dengan multi-gudang", "Tim finance yang tutup buku mingguan", "Grup yang konsolidasi laporan multi-entitas"],
    },
  },
  {
    slug: "bos",
    name: "BOS",
    fullName: "Business Operation System",
    code: "BOS",
    accent: "#d8ff5c",
    tagline: {
      en: "One operational backbone for routes, fleet, dispatch, and field operations.",
      id: "Satu tulang punggung operasional untuk rute, armada, dispatch, dan operasi lapangan.",
    },
    intro: {
      en: "BOS is our Business Operation System — designed for Indonesian logistics, distribution, and field-service operators who outgrew spreadsheets but still find generic ERPs too rigid. Built for archipelago realities: spotty connectivity, mixed device fleets, multi-island routing.",
      id: "BOS adalah Business Operation System kami — dirancang untuk operator logistik, distribusi, dan layanan lapangan Indonesia yang sudah melampaui spreadsheet namun masih merasa ERP generik terlalu kaku. Dibangun untuk realita kepulauan: konektivitas terputus-putus, armada device beragam, routing antar-pulau.",
    },
    capabilities: {
      en: [
        "Multi-island route planning & optimisation",
        "Real-time fleet & driver tracking",
        "Offline-first mobile dispatch app",
        "Proof-of-delivery (photo + signature + GPS)",
        "Service-level dashboards per region",
        "Open APIs for marketplace / e-commerce integration",
      ],
      id: [
        "Perencanaan & optimasi rute antar-pulau",
        "Pelacakan armada & driver real-time",
        "Aplikasi mobile dispatch offline-first",
        "Bukti pengiriman (foto + tanda tangan + GPS)",
        "Dashboard service-level per wilayah",
        "API terbuka untuk integrasi marketplace / e-commerce",
      ],
    },
    bestFor: {
      en: ["3PL & last-mile logistics", "FMCG distribution networks", "Field-service & maintenance crews", "Multi-region courier operators"],
      id: ["Logistik 3PL & last-mile", "Jaringan distribusi FMCG", "Tim layanan & pemeliharaan lapangan", "Operator kurir multi-wilayah"],
    },
  },
  {
    slug: "pos",
    name: "POS",
    fullName: "Point of Sale",
    code: "POS",
    accent: "#5b4bff",
    tagline: {
      en: "Cloud POS for cafés, retail, and F&B chains — built around Indonesian payment rails.",
      id: "POS berbasis cloud untuk kafe, ritel, dan jaringan F&B — dibangun di sekitar sistem pembayaran Indonesia.",
    },
    intro: {
      en: "A point-of-sale that respects how Indonesian retail actually runs: cashier ergonomics, QRIS-first checkout, table & queue management, and back-office reporting that the owner can read on a phone — without an MBA.",
      id: "POS yang menghormati cara ritel Indonesia bekerja: ergonomi kasir, checkout QRIS-first, manajemen meja & antrean, dan pelaporan back-office yang bisa dibaca pemilik dari ponsel — tanpa perlu MBA.",
    },
    capabilities: {
      en: [
        "QRIS, e-wallet, debit, and cash checkout",
        "Table, queue, and kitchen display (KDS) flows",
        "Inventory sync across outlets",
        "Discount, loyalty, and voucher engine",
        "Daily/weekly owner report on mobile",
        "Open shift & cash-drawer reconciliation",
      ],
      id: [
        "Checkout QRIS, e-wallet, debit, dan tunai",
        "Alur meja, antrean, & kitchen display (KDS)",
        "Sinkronisasi inventaris lintas outlet",
        "Engine diskon, loyalty, dan voucher",
        "Laporan harian/mingguan untuk owner via mobile",
        "Open shift & rekonsiliasi cash drawer",
      ],
    },
    bestFor: {
      en: ["Café & coffee shop chains", "Quick-service restaurants", "Boutique retail & concept stores", "Local F&B brands scaling outlets"],
      id: ["Jaringan kafe & coffee shop", "Quick-service restaurant", "Boutique retail & concept store", "Brand F&B lokal yang scaling outlet"],
    },
  },
  {
    slug: "procurement",
    name: "Procurement",
    fullName: "Procurement Suite",
    code: "PRC",
    accent: "#b8e637",
    tagline: {
      en: "End-to-end procure-to-pay with vendor scoring and clean audit trails.",
      id: "Procure-to-pay ujung-ke-ujung dengan skoring vendor dan jejak audit yang bersih.",
    },
    intro: {
      en: "Procurement is our P2P suite built for organisations that need defensible buying decisions: structured purchase requisitions, multi-tier approvals, vendor scoring, and contract repositories — all wired to ERP and payment systems.",
      id: "Procurement adalah suite procure-to-pay kami untuk organisasi yang membutuhkan keputusan pembelian yang dapat dipertanggungjawabkan: permintaan pembelian terstruktur, persetujuan multi-tingkat, skoring vendor, dan repositori kontrak — semuanya terhubung ke ERP dan sistem pembayaran.",
    },
    capabilities: {
      en: [
        "Purchase requisition & RFQ workflow",
        "Multi-tier approval matrix with delegation",
        "Vendor performance scoring & risk flags",
        "Contract repository with expiry alerts",
        "Spend analytics by category & cost-centre",
        "ERP / accounting integration (3-way match)",
      ],
      id: [
        "Alur kerja PR & RFQ",
        "Matriks persetujuan multi-tingkat dengan delegasi",
        "Skoring kinerja vendor & penanda risiko",
        "Repositori kontrak dengan peringatan kedaluwarsa",
        "Analitik pembelanjaan per kategori & cost-center",
        "Integrasi ERP / akuntansi (three-way match)",
      ],
    },
    bestFor: {
      en: ["Mid-to-large enterprises with audit obligations", "Public-sector & BUMN procurement teams", "Manufacturers with broad vendor base", "Groups consolidating procurement across entities"],
      id: ["Enterprise menengah-besar dengan kewajiban audit", "Tim procurement sektor publik & BUMN", "Manufaktur dengan basis vendor luas", "Grup yang mengkonsolidasi procurement antar-entitas"],
    },
  },
];
